import React, { useCallback } from 'react';
import { Sheet, TextArea } from 'tamagui';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  CobuStack,
  CobuText,
  CobuAlert,
  CobuHeader,
} from '@cobu/ui';
import { useReportUserMutation } from '../../hooks/userHooks';
import { Gathering } from '@cobu/types';
import { Strings } from '../../constants';
import { reportUserSchema, ReportUserFormValues } from '../../schemas/reportUserSchema';
import { logger } from '../../utils/logger';

const strings = Strings.get();

interface ReportUserModalProps {
  visible: boolean;
  onClose: () => void;
  reportedUserUuid: string;
  reportedByUuid: string;
  gathering?: Gathering;
  onSuccess?: () => void;
}

export function ReportUserModal({
  visible,
  onClose,
  reportedUserUuid,
  reportedByUuid,
  gathering,
  onSuccess
}: ReportUserModalProps) {
  const { control, handleSubmit, reset, formState } = useForm<ReportUserFormValues>({
    resolver: zodResolver(reportUserSchema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reportUserMutation = useReportUserMutation();

  const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);

  const isSubmitDisabled = !formState.isValid || reportUserMutation.isPending;

  const onSubmit = async ({ reason }: ReportUserFormValues) => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) return;

    try {
      await reportUserMutation.mutateAsync({
        reportedUserUuid,
        reportedByUuid,
        reason: trimmedReason,
        reportItem: { origin: 'groupEvent', groupEvent: gathering },
      });

      reset();
      // Close the modal first
      onClose();
      // Call onSuccess callback if provided (parent handles alert)
      // Otherwise show alert here (for backward compatibility)
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 200);
      } else {
        setTimeout(() => {
          setSuccessAlertOpen(true);
        }, 200);
      }
    } catch (error) {
      logger.error('Report submission error:', error);
      setErrorAlertOpen(true);
    }
  };

  const handleSubmitPress = useCallback(() => {
    if (isSubmitDisabled) return;
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit, isSubmitDisabled]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
    <Sheet
      open={visible}
      onOpenChange={(open: boolean) => !open && handleClose()}
      snapPoints={[80]}
      snapPointsMode="percent"
      dismissOnSnapToBottom
      dismissOnOverlayPress
      modal
    >
      <Sheet.Overlay
        animation={undefined as any}
        enterStyle={{ o: 0 }}
        exitStyle={{ o: 0 }}
        bg="$blackTransparent50"
      />
      <Sheet.Frame
        br="$3"
        gap="$4"
      >
        <CobuStack gap="$3">
          <CobuHeader
            renderLeft={() => (
              <CobuStack px="$1" py="$1" onPress={handleClose}>
                <CobuText variant="heading" fos="$4" fow="600" ta="center">
                  {strings.ui.reportUser.cancel}
                </CobuText>
              </CobuStack>
            )}
            titleComponent={() => (
              <CobuText variant="heading" fos="$4" fow="600" ta="center">
                {strings.ui.reportUser.title}
              </CobuText>
            )}
            renderRight={() => (
              <CobuStack
                px="$1"
                py="$1"
                opacity={isSubmitDisabled ? 0.4 : 1}
                onPress={handleSubmitPress}
              >
                <CobuText
                  variant="heading"
                  fos="$4"
                  fow="600"
                  ta="center"
                >
                  {strings.ui.reportUser.submit}
                </CobuText>
              </CobuStack>
            )}
          />

          {/* Content */}
          <CobuStack f={1} gap="$3" px="$4" py="$2">
            <Controller
              control={control}
              name="reason"
              render={({ field: { value, onChange, onBlur } }) => (
                <TextArea
                  placeholder={strings.common.placeholders.reportReason}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeholderTextColor="$colorMuted"
                  col="$color"
                  fos="$3"
                  p="$3"
                  mih={'$10'}
                  mah={'60%'}
                  bc="transparent"
                  borderWidth={1}
                  borderColor='$cardBorderColor'
                  multiline
                  numberOfLines={10}
                  maxLength={500}
                  autoFocus
                  verticalAlign="top"
                  autoCapitalize="sentences"
                  scrollEnabled
                  textWrap="wrap"
                  focusStyle={{
                    borderColor: '$black',
                    outlineWidth: 0,
                    outlineColor: 'transparent',
                    shadowColor: 'transparent',
                    borderWidth: 1,
                  }}
                />
              )}
            />
          </CobuStack>
        </CobuStack>
      </Sheet.Frame>
    </Sheet>
    <CobuAlert
      open={successAlertOpen}
      onOpenChange={setSuccessAlertOpen}
      title={strings.ui.reportUser.userReported}
      description={strings.ui.reportUser.userReportedMessage}
      onAccept={() => {
        setSuccessAlertOpen(false);
        handleClose();
      }}
    />
    <CobuAlert
      open={errorAlertOpen}
      onOpenChange={setErrorAlertOpen}
      title={strings.common.error.title}
      description={strings.ui.reportUser.submitError}
      onAccept={() => setErrorAlertOpen(false)}
    />
  </>
  );
}
